import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import {
  Alert,
  Button,
  Modal,
  SelectField,
  TextField,
  mutedTextClass,
} from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { fieldErrorFor, isFormLevel, messageFor } from '@/lib/errors'
import { queryKeys } from '@/lib/queryClient'
import { adminApi } from './api'
import type { AdminWorkspace } from './types'

/**
 * "Grant plan" — comp a workspace onto a paid tier without it paying.
 *
 * Three things about this dialog are deliberate.
 *
 * The REASON is required, and the field says why rather than just being marked
 * with an asterisk. Six months from now, a workspace on Business with no
 * subscription is either a grandfathered migration, a support credit or a
 * mistake, and the only thing that will ever tell them apart is what somebody
 * typed here. The server refuses a grant without it, so this is a courtesy, not
 * the enforcement.
 *
 * The DURATION defaults to 90 days rather than to "no expiry". A lifetime comp
 * is a legitimate thing to issue and an easy thing to issue by accident; making
 * it the deliberate choice at the bottom of the list is the difference.
 *
 * The dialog states plainly what happens on an existing plan. Grants stack as
 * rows and the newest in force wins, so comping a workspace that is already
 * paying Stripe does not cancel anything — and someone doing this at speed
 * should not have to know that from memory.
 */
const PLANS = [
  { id: 'standard', label: 'Standard' },
  { id: 'business', label: 'Business' },
  { id: 'unlimited', label: 'Unlimited' },
  { id: 'pro', label: 'Pro (App Store tier — uncapped seats)' },
]

const DURATIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: 'date', label: 'Until a specific date…' },
  { value: 'forever', label: 'No expiry (lifetime comp)' },
]

export function GrantPlanDialog({
  workspace,
  open,
  onClose,
}: {
  workspace: AdminWorkspace | null
  open: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [plan, setPlan] = useState('business')
  const [duration, setDuration] = useState('90')
  const [until, setUntil] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) return
    setPlan('business')
    setDuration('90')
    setUntil('')
    setReason('')
  }, [open])

  const grant = useMutation({
    mutationFn: () => {
      if (!workspace) throw new Error('No workspace selected.')
      return adminApi.grant(workspace.slug, {
        plan,
        reason: reason.trim(),
        // Omitting BOTH days and pro_until is what the API reads as an
        // open-ended comp, so 'forever' contributes nothing rather than a zero.
        //
        // A date input yields a day, and the API parses a full timestamp: the
        // expiry is anchored to the end of that day in UTC, because anchoring
        // it to midnight would end the comp a day earlier than the person
        // picking the date meant.
        ...(duration === 'date'
          ? { pro_until: `${until}T23:59:59.000000Z` }
          : duration === 'forever'
            ? {}
            : { days: Number(duration) }),
      })
    },
    onSuccess: async () => {
      // Every console read can now be stale: the plan column, the overview
      // counts and this workspace's detail.
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin })
      onClose()
    },
  })

  // Guarded rather than relying on the disabled button: pressing Enter in a
  // text field submits the form directly, and this is a request that hands out
  // a paid plan.
  const ready = reason.trim().length > 0 && (duration !== 'date' || until !== '')

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!ready || grant.isPending) return
    grant.mutate()
  }

  if (!workspace) return null

  const alreadyPaid = workspace.plan_source === 'stripe' || workspace.plan_source === 'apple'
  const fields = ['plan', 'reason', 'pro_until']

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Grant a plan to ${workspace.name}`}
      description="This gives the workspace a paid plan without it paying. It takes effect immediately."
      footer={
        <>
          <Button onClick={onClose} disabled={grant.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={grant.isPending}
            disabled={!ready}
          >
            Grant plan
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {alreadyPaid ? (
          <Alert tone="warning">
            This workspace already has a {workspace.plan_source === 'apple' ? 'App Store' : 'Stripe'}{' '}
            subscription. A grant does not cancel it — they keep being billed, and whichever
            entitlement is better applies.
          </Alert>
        ) : null}

        {isFormLevel(grant.error, fields) && grant.error ? (
          <Alert tone="danger">{messageFor(grant.error)}</Alert>
        ) : null}

        <SelectField
          label="Plan"
          value={plan}
          error={fieldErrorFor(grant.error, 'plan')}
          onChange={(event) => setPlan(event.target.value)}
        >
          {PLANS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Duration"
          value={duration}
          hint={
            duration === 'forever'
              ? 'The workspace keeps this plan until someone revokes the grant.'
              : undefined
          }
          onChange={(event) => setDuration(event.target.value)}
        >
          {DURATIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>

        {duration === 'date' ? (
          <TextField
            label="Expires on"
            type="date"
            value={until}
            error={fieldErrorFor(grant.error, 'pro_until')}
            onChange={(event) => setUntil(event.target.value)}
          />
        ) : null}

        <TextField
          label="Reason"
          value={reason}
          maxLength={255}
          placeholder="Grandfathered from the legacy plan"
          error={fieldErrorFor(grant.error, 'reason')}
          hint="Required. This is recorded against your name in the audit log, and it is the only thing that will explain this comp later."
          onChange={(event) => setReason(event.target.value)}
        />
      </form>
    </Modal>
  )
}

/**
 * Revoking a grant is destructive in the way that matters: a customer who was
 * on Business loses features mid-sprint. The dialog therefore names the
 * workspace, says exactly where it will land, and asks for its own reason.
 */
export function RevokeGrantDialog({
  workspace,
  open,
  onClose,
}: {
  workspace: AdminWorkspace | null
  open: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  const revoke = useMutation({
    mutationFn: () => {
      if (!workspace?.grant) throw new Error('No grant to revoke.')
      return adminApi.revokeGrant(workspace.grant.id, reason.trim())
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin })
      onClose()
    },
  })

  if (!workspace?.grant) return null

  const paysProvider =
    workspace.subscription_status === 'active' || workspace.subscription_status === 'trialing'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Revoke the grant on ${workspace.name}`}
      footer={
        <>
          <Button onClick={onClose} disabled={revoke.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => revoke.mutate()}
            loading={revoke.isPending}
            disabled={reason.trim().length === 0}
          >
            Revoke grant
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert tone={paysProvider ? 'info' : 'warning'}>
          {paysProvider
            ? 'This workspace also has a live subscription, so it will keep the plan that subscription pays for.'
            : 'This workspace has no subscription behind the grant, so it will drop to Free immediately and lose paid features.'}
        </Alert>

        <p className={cn('text-sm', mutedTextClass)}>
          The grant is kept and marked as revoked, so the record of who comped this workspace and why
          survives.
        </p>

        {revoke.error ? <Alert tone="danger">{messageFor(revoke.error)}</Alert> : null}

        <TextField
          label="Reason"
          value={reason}
          maxLength={255}
          placeholder="Trial ended, no purchase"
          error={fieldErrorFor(revoke.error, 'reason')}
          hint="Required. Recorded in the audit log against your name."
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  )
}
